/**
 * The real WebAudio graph: `docs/engineering/audio-plan.md` §4's three-bus design —
 *
 * ```
 * AudioContext
 *  └─ pauseGain ──> destination   (`pause-gate.ts`: 1 while flying, 0 while the sim is stopped)
 *      └─ masterGain
 *          ├─ musicGain   ──> (unused in flight — see note below; wired for title-screen reuse)
 *          ├─ sfxGain     ──> pooled one-shots: gunfire (one crack per round), dead-trigger clicks,
 *          │                  hit/jam/warning/message cues
 *          └─ engineGain  ──> the OPL2 engine voice (`AudioWorkletNode`), wind bed, fire bed +
 *                             crackle pops, stall-rush bed, dive whine
 * ```
 *
 * The **dive whine** is the one layer on that bus the original does not have at
 * all: a declared port invention (user, 2026-08-26) riding ENHANCED EFFECTS and
 * silent when that row is off. Everything about it — what it models, what is
 * invented, and the per-band level measurement that set its two gains — is in
 * `dive-whine.ts`; it is on `engineGain` so it obeys the same master/sfx/mute
 * settings and the same pause gate as the wind bed it sits beside.
 *
 * `pauseGain` is a separate node from `masterGain` rather than a second writer of the same gain so
 * the volume slider and the pause fade cannot overwrite each other: `setSettings` owns one,
 * `setPaused` owns the other, and either can move at any time.
 *
 * **In-flight music stays silent** — `docs/engineering/audio-plan.md` §1 confirms Red Baron
 * shipped zero in-flight background music (all 31 `SIMSNDS.SNG` resources are SFX), so this
 * engine never plays anything on `musicGain`; that bus and its settings value exist purely so
 * `Home.tsx`'s title-theme `<audio>` element can read/share the same persisted `music` slider
 * (`settings.ts`) without a second, disconnected volume model.
 *
 * The settings popover only exposes master/music/sfx sliders (the brief's "simple" sliders) —
 * there is no fourth "engine ambience" slider, so `engineGain` is deliberately driven by the same
 * `sfx` value as `sfxGain`. To a player, engine/wind/fire/gunfire read as "sound effects" same as
 * a message ding; splitting them into a bus the user can't independently reach would just be
 * another knob nobody asked for while still keeping the internal 3-bus graph the plan calls for.
 *
 * Every persistent *sample* loop (wind, fire, stall-rush) sets
 * `AudioBufferSourceNode.loopStart`/`.loopEnd` from `continuous-params.ts`'s `loopRegionFor`
 * instead of looping the whole buffer — the source plays its attack once, then repeats only the
 * steady tail, matching the ten SCI loop-marked cues' actual gate-style shape instead of creating
 * a re-attack seam every pass. Two of these loops are gain-*gated* by a boolean game state
 * (`burning`, `stalled`) via `advanceGateRamp`, not just volume-scaled by a continuous value the
 * way wind is.
 *
 * **Corrected (2026-08-19, ENGINE SOUND lane).** A live OPL-register capture of `RB.EXE`
 * (`docs/formats/sound-music.md` § "Continuous engine sound", `docs/engineering/audio-plan.md` §6)
 * retired three things this module used to do:
 * - **The engine is a real OPL2 voice now**, not synthesis and not a sample: bank program 4 keyed
 *   on once for the whole flight while its pitch tracks a lagged RPM state. It runs on the audio
 *   thread (`engine-worklet-node.ts` → `engine-worklet.ts` → `engine-voice-dsp.ts`) because the
 *   glide between pitch steps is the sound's most characteristic behaviour and no crossfade of
 *   pre-rendered buffers reproduces it. The procedural synth it replaced moved to
 *   `engine-synth-parked.ts`, still built but never started, and is deliberately *not* the
 *   worklet's fallback.
 * - **The "engine catches" accent is gone.** Cue 0 was never played in any of the four captured
 *   flights.
 * - **Cue 2 is not gunfire**, it is the engine — so the gain-ramped held-fire loop is gone too.
 *
 * **Corrected again (2026-08-21, GUN AUDIO lane).** A capture taken *with the guns firing* — the
 * one the note above said was still missing — settles gunfire outright
 * (`docs/formats/sound-music.md` § "Gunfire, jams and hits"). Held fire is now one `SIMSNDS-01`
 * crack per round at the measured `GUN_SHOT_INTERVAL_S` (8.47 rounds/s), unjittered, and a trigger
 * held on jammed or empty guns plays the measured `DEAD_TRIGGER_CUE` click clusters instead of
 * nothing at all. Cue 16, which used to be the whole of gunfire here, was never played by the
 * original in any captured flight.
 *
 * This module is the one part of `audio/` that is *not* unit-tested directly (it needs a real
 * `AudioContext`, which `bun test` has no DOM to provide) — the pure math it calls into
 * (`continuous-params.ts`, `sfx-map.ts`, `settings.ts`, `buffer-cache.ts`) is. It is instead
 * verified by loading `/fly` in a real (headless) browser and asserting zero console errors with
 * this module active, per `docs/engineering/lessons.md`'s browser-verification rule for WebGPU
 * routes — the same reasoning applies to WebAudio: a wrong-but-non-throwing graph would not show
 * up in `bun test` at all.
 */

import {
  advanceCluster,
  advanceGateRamp,
  advanceRetrigger,
  CLUSTER_IDLE,
  cycleCue,
  EMPTY_TRIGGER_CLUSTER,
  FIRE_CRACKLE_INTERVAL_S,
  FIRE_LOOP_RAMP_S,
  GATE_RAMP_IDLE,
  GUN_SHOT_INTERVAL_S,
  JAMMED_TRIGGER_CLUSTER,
  RETRIGGER_IDLE,
  risingEdge,
  STALL_RUSH_RAMP_S,
  windGain,
  windRate,
  type ClusterState,
  type GateRampState,
  type RetriggerState,
} from "./continuous-params.ts";
import { DEAD_TRIGGER_CUE, FIRE_CRACKLE_POP_CUES, FIRE_LOOP_CUE, GUN_SHOT_CUE, sfxForMessage, STALL_RUSH_LOOP_CUE, WIND_LOOP_CUE } from "./sfx-map.ts";
import { createBufferCache, type BufferCache } from "./buffer-cache.ts";
import { createDiveWhine, disposeDiveWhine, updateDiveWhine, type DiveWhineHandle } from "./dive-whine.ts";
import { createEngineSynth, disposeEngineSynth, updateEngineSynth, type EngineSynthHandle } from "./engine-synth-parked.ts";
import { enemyLevelIndexForRange, engineTargetIndex } from "./engine-voice.ts";
import { createEngineVoice, type EngineVoiceHandle } from "./engine-worklet-node.ts";
import { createEnemyGunVoicePool, type EnemyGunVoicePool } from "./enemy-gun-audio.ts";
import { createFlakBurstVoicePool, type FlakBurstVoicePool } from "./flak-audio.ts";
import { createPauseGate, type PauseGate } from "./pause-gate.ts";
import { playOneShot, startLoop, type LoopHandle } from "./playback.ts";
import { clampVolume, DEFAULT_AUDIO_SETTINGS, type AudioSettings } from "./settings.ts";
import type { SfxCueId } from "./simsnds-cues.ts";

export interface ContinuousFrameParams {
  /** 0..1, smoothed throttle actually reaching the engine (`SimState.engine.throttle`). */
  readonly throttle: number;
  /** 0..1, 1 = undamaged (`SimState.damage.engine`). */
  readonly engineHealth: number;
  readonly airspeedMps: number;
  readonly burning: boolean;
  /**
   * Rounds are actually leaving the guns this frame — i.e. the trigger is down *and* the belt is
   * not empty *and* the guns are not jammed. Read every frame, not an event.
   */
  readonly firing: boolean;
  /**
   * The trigger itself (`FlightInputs.firing`), ungated by ammunition or jam state.
   *
   * Optional so a caller that has not been updated simply gets no dead-trigger clicks rather than
   * a type error. When it is `true` while `firing` is `false`, `engine.ts` plays the measured
   * dead-trigger click cluster — the original's own answer to "you pulled the trigger and nothing
   * came out" (`sfx-map.ts`'s `DEAD_TRIGGER_CUE`).
   */
  readonly triggerDown?: boolean;
  /** `SimState.guns.jammed` — picks `JAMMED_TRIGGER_CLUSTER` over `EMPTY_TRIGGER_CLUSTER`; the original's two dead-trigger patterns differ in click count and period. */
  readonly gunsJammed?: boolean;
  /** `SimState.readout.stalled` — read every frame, not an event, same as `firing`/`burning`. */
  readonly stalled: boolean;
  /**
   * Slant range to the nearest other aircraft in metres, or `null`/absent when none is in the air.
   *
   * **Optional on purpose.** It drives the original's distance model — a second OPL2 voice at a
   * fixed pitch whose carrier level steps down a measured 16-rung ladder as an aircraft closes
   * (`engine-voice.ts`'s `ENEMY_LEVEL_LADDER`) — and the whole path down to the chip is wired and
   * tested. Leaving it out simply keeps that second voice keyed off, so `flight-scene.ts` (another
   * lane's file) can start passing it in a one-line change without this module needing to move
   * first.
   */
  readonly nearestEnemyRangeM?: number | null;
  /**
   * Distance in metres from the player to each flak burst that went off this frame — the same
   * per-tick `FlakBurst` list `flight/encounter-views.ts`'s `effectSpawnsOf` draws a `flakBurst`
   * sprite from (`combat/flak.ts`: bursts are an output, not carried state, so this is exactly
   * "how many shells went off this frame, and how far away"). Empty or absent when none did.
   * `flak-audio.ts`'s `FlakBurstVoicePool` turns each entry into a distance-attenuated one-shot,
   * sight and sound keyed off the identical list the renderer uses.
   */
  readonly flakBurstDistancesM?: readonly number[];
  /**
   * Slant range in metres to every *other* aeroplane whose guns fired this frame —
   * `flight/frame-signals.ts`'s `firingOtherAircraftRangesM`, off the same
   * `AircraftView` list the renderer draws those aeroplanes from. Empty or absent
   * when nobody else is shooting, which is most frames.
   *
   * `enemy-gun-audio.ts`'s `EnemyGunVoicePool` turns each into a
   * distance-attenuated crack of `ENEMY_GUN_CUE`. **Optional on the same policy
   * as `flakBurstDistancesM` and `nearestEnemyRangeM`** — a caller that has not
   * been updated simply hears no enemy guns rather than getting a type error.
   */
  readonly firingAircraftRangesM?: readonly number[];
  /**
   * `EffectsSettings.enhancedEffects` — the DISPLAY row the dive whine
   * (`dive-whine.ts`) rides, read every frame so flipping it mid-flight fades
   * the layer without a reload.
   *
   * **Optional, and absent means off**, the same policy `nearestEnemyRangeM`
   * takes above and for the same reason: the whine is a declared invention, so a
   * caller that has not opted into it gets the original's own soundscape rather
   * than a sound the 1990 game never made.
   */
  readonly enhancedEffects?: boolean;
}

export interface FlightAudioEngine {
  /** Call on the first pointer/key event inside `/fly` — defensive against autoplay-suspended contexts. */
  resume(): void;
  setSettings(settings: AudioSettings): void;
  /**
   * Silences (or restores) the entire mix, in step with the sim's own pause
   * (`flight-scene.ts`'s `setPaused`). While paused, `updateFrame` is a no-op
   * as well as inaudible: a frozen sim has no new frame to sound, and letting
   * the gun retrigger keep accumulating would fire the whole held burst the
   * instant the flight resumed.
   */
  setPaused(paused: boolean): void;
  /** One HUD-message string per line this tick, e.g. `FlightFrame.messages`. */
  handleMessages(messages: readonly string[]): void;
  updateFrame(dtS: number, params: ContinuousFrameParams): void;
  dispose(): void;
}

/** `AudioContext` isn't available in headless test/CI contexts — same feature-detect as `AudioViewer.tsx`'s `hasAudioContext()`. */
function hasAudioContext(): boolean {
  return typeof window !== "undefined" && typeof window.AudioContext === "function";
}

const NOOP_ENGINE: FlightAudioEngine = {
  resume() {},
  setSettings() {},
  setPaused() {},
  handleMessages() {},
  updateFrame() {},
  dispose() {},
};

/**
 * ±this fraction of playbackRate randomization per fire-crackle pop, so a retriggered pop pool
 * doesn't sound like one sample on repeat. **Gunfire deliberately does not use it** — the capture
 * shows the original writing byte-identical registers for every round (`updateGunfire`).
 */
const PITCH_JITTER = 0.08;

function jitteredRate(): number {
  return 1 + (Math.random() * 2 - 1) * PITCH_JITTER;
}

/**
 * Owns the graph, the decode cache and the gate/retrigger accumulators. A class rather than a
 * closure-returning factory purely so the per-method logic (`updateFrame` especially) stays under
 * the project's function-length bar without artificially splitting one cohesive frame update into
 * unrelated free functions — the state it reads/writes (the gate-ramp/retrigger states, the play
 * counts, the loop handles) becomes fields instead of closed-over `let`s.
 */
class FlightAudioEngineImpl implements FlightAudioEngine {
  private disposed = false;
  private readonly pauseGain: GainNode;
  private readonly pauseGate: PauseGate;
  private readonly masterGain: GainNode;
  private readonly musicGain: GainNode;
  private readonly sfxGain: GainNode;
  private readonly engineGain: GainNode;
  private readonly cache: BufferCache<AudioBuffer>;
  private readonly engineSynth: EngineSynthHandle;
  private readonly engineVoice: EngineVoiceHandle;
  private readonly windLoop: LoopHandle;
  private readonly fireLoop: LoopHandle;
  private readonly stallRushLoop: LoopHandle;
  private readonly diveWhine: DiveWhineHandle;
  private gunWasFiring = false;
  private gunRetrigger: RetriggerState = RETRIGGER_IDLE;
  private deadTrigger: ClusterState = CLUSTER_IDLE;
  private fireLoopState: GateRampState = GATE_RAMP_IDLE;
  private stallRushState: GateRampState = GATE_RAMP_IDLE;
  private firePopRetrigger: RetriggerState = RETRIGGER_IDLE;
  private firePopPlayCount = 0;
  private readonly flakBursts: FlakBurstVoicePool;
  private readonly enemyGuns: EnemyGunVoicePool;

  constructor(
    private readonly context: AudioContext,
    initialSettings: AudioSettings,
  ) {
    this.pauseGain = context.createGain();
    this.pauseGain.gain.value = 1;
    this.pauseGain.connect(context.destination);
    this.pauseGate = createPauseGate(this.pauseGain.gain, () => context.currentTime);
    this.masterGain = context.createGain();
    this.masterGain.connect(this.pauseGain);
    this.musicGain = context.createGain();
    this.musicGain.connect(this.masterGain);
    this.sfxGain = context.createGain();
    this.sfxGain.connect(this.masterGain);
    this.engineGain = context.createGain();
    this.engineGain.connect(this.masterGain);

    this.cache = createBufferCache<ArrayBuffer, AudioBuffer>({
      fetchBytes: async (path) => {
        const response = await fetch(path);
        if (!response.ok) throw new Error(`failed to load audio ${path}: ${response.status} ${response.statusText}`);
        return response.arrayBuffer();
      },
      decode: (bytes) => context.decodeAudioData(bytes),
    });

    const isDisposed = () => this.disposed;
    this.engineSynth = createEngineSynth(context, this.engineGain);
    this.engineVoice = createEngineVoice(context, this.engineGain);
    this.windLoop = startLoop(context, this.cache, WIND_LOOP_CUE, this.engineGain, isDisposed);
    this.fireLoop = startLoop(context, this.cache, FIRE_LOOP_CUE, this.engineGain, isDisposed);
    this.stallRushLoop = startLoop(context, this.cache, STALL_RUSH_LOOP_CUE, this.engineGain, isDisposed);
    this.flakBursts = createFlakBurstVoicePool(context, this.cache, this.sfxGain, isDisposed);
    this.enemyGuns = createEnemyGunVoicePool(context, this.cache, this.sfxGain, isDisposed);
    this.diveWhine = createDiveWhine(context, this.engineGain);

    this.setSettings(initialSettings);
  }

  resume(): void {
    if (this.disposed) return;
    if (this.context.state === "suspended") void this.context.resume().catch(() => {});
  }

  setPaused(paused: boolean): void {
    if (this.disposed) return;
    this.pauseGate.setPaused(paused);
  }

  setSettings(settings: AudioSettings): void {
    this.masterGain.gain.value = settings.muted ? 0 : clampVolume(settings.master);
    this.musicGain.gain.value = clampVolume(settings.music);
    this.sfxGain.gain.value = clampVolume(settings.sfx);
    this.engineGain.gain.value = clampVolume(settings.sfx);
  }

  handleMessages(messages: readonly string[]): void {
    if (this.disposed || this.pauseGate.paused()) return;
    for (const message of messages) {
      const cue = sfxForMessage(message);
      if (cue) this.playOneShot(cue, this.sfxGain, 1);
    }
  }

  updateFrame(dtS: number, params: ContinuousFrameParams): void {
    if (this.disposed || this.pauseGate.paused()) return;
    this.updateEngine(params);
    this.updateWindLoop(params);
    updateDiveWhine(this.diveWhine, params.airspeedMps, params.enhancedEffects === true, this.context.currentTime);
    this.updateGunfire(dtS, params);
    this.updateFireLoop(dtS, params.burning);
    this.updateFireCracklePops(dtS, params.burning);
    this.updateStallRush(dtS, params.stalled);
    this.flakBursts.trigger(params.flakBurstDistancesM);
    this.enemyGuns.update(dtS, params.firingAircraftRangesM);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    disposeEngineSynth(this.engineSynth);
    this.engineVoice.dispose();
    this.windLoop.source?.stop();
    this.fireLoop.source?.stop();
    this.stallRushLoop.source?.stop();
    disposeDiveWhine(this.diveWhine);
    this.pauseGain.disconnect();
    this.masterGain.disconnect();
    this.musicGain.disconnect();
    this.sfxGain.disconnect();
    this.engineGain.disconnect();
    void this.context.close().catch(() => {});
  }

  /**
   * Two numbers a frame, and nothing else. The voice's level, envelope and
   * key state are fixed for the whole flight (`engine-voice.ts`) — engine
   * *damage* deliberately has no audio effect here, because the capture never
   * caught a damaged-but-flying engine and inventing one would be exactly the
   * kind of unmeasured embellishment §6 warns against. `engineHealth` still
   * reaches the parked synth so its curves stay exercised.
   */
  private updateEngine(params: ContinuousFrameParams): void {
    updateEngineSynth(this.engineSynth, params.throttle, params.engineHealth);
    this.engineVoice.setTargetIndex(engineTargetIndex(params.throttle, params.airspeedMps));
    this.engineVoice.setEnemyLevelIndex(enemyLevelIndexForRange(params.nearestEnemyRangeM));
  }

  private updateWindLoop(params: ContinuousFrameParams): void {
    this.windLoop.gainNode.gain.value = windGain(params.airspeedMps);
    if (this.windLoop.source) this.windLoop.source.playbackRate.value = windRate(params.airspeedMps);
  }

  /**
   * MEASURED gunfire (`sfx-map.ts`'s `GUN_SHOT_CUE`, `docs/formats/sound-music.md`
   * § "Gunfire, jams and hits"): one cue-1 crack on the trigger's rising edge and
   * one every `GUN_SHOT_INTERVAL_S` after that — 8.47 rounds/s, the cadence the
   * original's own OPL key-on stream runs at, on the twin-gun Camel and the
   * single-gun S.E.5a alike.
   *
   * **Deliberately no `jitteredRate()`.** The capture shows every shot writing the
   * identical registers at the identical pitch; the ±8 % playback-rate wobble the
   * cue-16 rattle needed to stop sounding like one sample on repeat is exactly the
   * kind of embellishment the original does not have, and at 118 ms spacing a
   * single 107 ms crack per round already reads as a machine gun without it.
   *
   * When the trigger is down but nothing is coming out — jammed, or an empty belt —
   * the same input drives the dead-trigger click cluster instead.
   */
  private updateGunfire(dtS: number, params: ContinuousFrameParams): void {
    const firing = params.firing;
    if (risingEdge(this.gunWasFiring, firing)) this.playOneShot(GUN_SHOT_CUE, this.sfxGain, 1);
    this.gunWasFiring = firing;
    const shot = advanceRetrigger(this.gunRetrigger, dtS, firing, GUN_SHOT_INTERVAL_S);
    this.gunRetrigger = shot.next;
    if (shot.fire) this.playOneShot(GUN_SHOT_CUE, this.sfxGain, 1);

    const dead = (params.triggerDown ?? false) && !firing;
    const cluster = advanceCluster(this.deadTrigger, dtS, dead, params.gunsJammed === true ? JAMMED_TRIGGER_CLUSTER : EMPTY_TRIGGER_CLUSTER);
    this.deadTrigger = cluster.next;
    if (cluster.fire) this.playOneShot(DEAD_TRIGGER_CUE, this.sfxGain, 1);
  }

  private updateFireLoop(dtS: number, burning: boolean): void {
    const result = advanceGateRamp(this.fireLoopState, dtS, burning, FIRE_LOOP_RAMP_S);
    this.fireLoopState = result.next;
    this.fireLoop.gainNode.gain.value = result.gain;
  }

  private updateFireCracklePops(dtS: number, burning: boolean): void {
    const result = advanceRetrigger(this.firePopRetrigger, dtS, burning, FIRE_CRACKLE_INTERVAL_S);
    this.firePopRetrigger = result.next;
    if (!result.fire) return;
    this.playOneShot(cycleCue(FIRE_CRACKLE_POP_CUES, this.firePopPlayCount), this.engineGain, jitteredRate());
    this.firePopPlayCount++;
  }

  private updateStallRush(dtS: number, stalled: boolean): void {
    const result = advanceGateRamp(this.stallRushState, dtS, stalled, STALL_RUSH_RAMP_S);
    this.stallRushState = result.next;
    this.stallRushLoop.gainNode.gain.value = result.gain;
  }

  private playOneShot(cue: SfxCueId, bus: GainNode, rate: number): void {
    playOneShot(this.context, this.cache, cue, bus, rate, () => this.disposed);
  }
}

export function createFlightAudioEngine(initialSettings: AudioSettings = DEFAULT_AUDIO_SETTINGS): FlightAudioEngine {
  if (!hasAudioContext()) return NOOP_ENGINE;
  try {
    return new FlightAudioEngineImpl(new AudioContext(), initialSettings);
  } catch {
    // Some headless/CI environments advertise the constructor but throw on use (no audio device).
    return NOOP_ENGINE;
  }
}
